import { useMemo, useState, useCallback } from 'react';
import { CheckboxChangeEvent } from 'antd/es/checkbox';

interface UseSelectionProps<T> {
	data: T[];
	totalItems: number;
	onClearSelection: () => void;
	getItemId: (item: T) => string;
}

export function useCheckboxSelection<T>({ data, onClearSelection, getItemId }: Readonly<UseSelectionProps<T>>) {
	const [selectedIds, setSelectedIds] = useState<string[]>([]);
	const currentPageIds = useMemo(() => data.map(getItemId), [data, getItemId]);

	const selectedRowKeys = useMemo(() => {
		return selectedIds.filter((id) => currentPageIds.includes(id));
	}, [currentPageIds, selectedIds]);

	const isAllSelected = useMemo(() => {
		if (currentPageIds.length === 0) return false;
		return currentPageIds.every((id) => selectedIds.includes(id));
	}, [currentPageIds, selectedIds]);

	const isIndeterminate = useMemo(() => {
		if (currentPageIds.length === 0) return false;
		const selectedInPage = currentPageIds.filter((id) => selectedIds.includes(id)).length;
		return selectedInPage > 0 && selectedInPage < currentPageIds.length;
	}, [currentPageIds, selectedIds]);

	const handleMainCheckboxChange = useCallback(
		(e: CheckboxChangeEvent) => {
			e.stopPropagation();
			const { checked } = e.target;

			setSelectedIds((prev) => {
				const newSelectedIds = new Set(prev);
				currentPageIds.forEach((id) => {
					if (checked) {
						newSelectedIds.add(id);
					} else {
						newSelectedIds.delete(id);
					}
				});
				return Array.from(newSelectedIds);
			});
		},
		[currentPageIds]
	);

	const handleRowCheckboxChange = useCallback((id: string, checked: boolean) => {
		const rowIds = id.includes(',') ? id.split(',') : [id];

		setSelectedIds((prev) => {
			const newSelectedIds = new Set(prev);
			rowIds.forEach((rowId) => {
				if (checked) {
					newSelectedIds.add(rowId);
				} else {
					newSelectedIds.delete(rowId);
				}
			});
			return Array.from(newSelectedIds);
		});
	}, []);

	const clearSelection = useCallback(() => {
		setSelectedIds([]);
		onClearSelection();
	}, [onClearSelection]);

	return {
		selectedIds,
		totalSelectedCount: selectedIds.length,
		selectedRowKeys,
		handleMainCheckboxChange,
		handleRowCheckboxChange,
		clearSelection,
		isLoading: false,
		isAllSelected,
		isIndeterminate
	};
}
